import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./AddGroupPluginModal.module.css";
import { Modal } from "shared/Modal";
import ModalActionBar from "shared/ModalActionBar";
import CheckBoxItem from "DreamStream/Shared/CheckBoxItem";
import SuggestionInput from "shared/@forms/SuggestionInput";
import { useIBOSearch } from "./useIBOSearch";
import { useGroupPlugins } from "DreamStream/PluginsPage/usePlugins";
import classNames from "classnames";

/**
 * Interactive component.
 * Modal used for adding a group plugin.
 */

export function AddGroupPluginModal({ isOpen, onClose, onClick }) {
  const { groupPlugins, loading } = useGroupPlugins();

  const [newPlugin, setNewPlugin] = useState({
    isAdmin: false,
    sync: false,
    // withoutRefresh: false,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const { searchResults, isLoading } = useIBOSearch(searchTerm);
  const [editTerm, setEditTerm] = useState("");
  const [selectedIBO, setSelectedIBO] = useState("");
  const [selectedOrg, setSelectedOrg] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState([]);
  const [newGroupPlugin, setNewGroupPlugin] = useState([]);
  const [errors, setErrors] = useState({
    selectedGroup: "",
    selectedIBO: "",
  });
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [newFilter, setNewFilter] = useState("");
  const [openDropDown, setOpenDropdown] = useState(false);

  const [searchData, setSearchData] = useState([]);

  // set filtered data when group search query changes
  useEffect(() => {
    if (newFilter) {
      setSearchData(filteredData(sortedData, newFilter));
    }
  }, [newFilter]);

  const ref = useRef(null);

  const handleDropdownClick = () => {
    setOpenDropdown(!openDropDown);
    ref.current.focus();
  };

  const groupsData = groupPlugins?.filter(
    (org) => org.groups.length > 0 
  );
  const sortedData = groupsData?.reduce((acc, org) => {
    org.groups.forEach((group) => {
      const newOrgEntry = { ...org, groups: [group] };
      acc.push(newOrgEntry);
    });
    return acc;
  }, []);

  // Reset modal when closed.
  useEffect(() => {
    if (!isOpen) {
      setNewPlugin({ isAdmin: false, syncUser: false, withoutRefresh: false });
      setEditTerm("");
      setSelectedIBO("");
      setSearchTerm("");
      setSelectedGroup([]);
      setSelectedOrg([]);
      setErrors({ selectedGroup: "", selectedIBO: "" });
    }
  }, [isOpen]);

  function filteredData(sortedData, newFilter) {
    const suggestGroup = sortedData.filter(
      (org) =>
        (org.groups &&
          org.groups.some((group) =>
            group.groupName
              .toLowerCase()
              .includes(newFilter.trim().toLowerCase())
          )) ||
        (org.organizationName &&
          org.organizationName
            .toLowerCase()
            .includes(newFilter.trim().toLowerCase())) ||
        (org.iboNumber && String(org.iboNumber).includes(newFilter.trim()))
    );

    return suggestGroup;
  }

  //IBO select onClick
  const iboSearchSelect = (item) => {
    setSelectedOrg(item);

    setSelectedIBO(item.display);
  };

  //Group select onClick
  const groupSearchSelect = (group) => {
    setNewGroupPlugin(group);
    setSelectedGroup([
      group.iboNumber,
      group.organizationName,
      group.groups[0].groupName,
    ]);

    setNewFilter("");
    setOpenDropdown(false);
  };

  // Error checking
  function validateForm() {
    let tempErrors = {};

    if (selectedIBO.length < 1 || selectedIBO === "") {
      tempErrors = {
        ...tempErrors,
        selectedIBO: "Please select a name or IBO number.",
      };
    }

    if (selectedGroup.length < 1 || selectedGroup === "") {
      tempErrors = {
        ...tempErrors,
        selectedGroup: "Please enter a group name.",
      };
    }

    if (selectedOrg.iboNumber === String(newGroupPlugin.iboNumber)) {
      tempErrors = {
        ...tempErrors,
        selectedGroup: "Cannot plug IBO into the same IBO.",
      };
    }

    const matchedIboData = groupPlugins?.filter(
      (data) =>
        data.iboNumber === Number(selectedOrg.iboNumber) && data.pinLevel === 0
    );

    if (matchedIboData && matchedIboData.length > 0) {
      const existingPlugin = matchedIboData.some((data) =>
        data.plugins?.some((plugin) =>
          plugin.groups?.some(
            (group) => group.groupId === newGroupPlugin.groups[0].groupId
          )
        )
      );

      if (existingPlugin) {
        tempErrors = {
          ...tempErrors,
          selectedGroup: "This group already exists. Add a different group.",
        };
      }
    }

    setErrors(tempErrors);
    const hasErrors = Object.keys(tempErrors).length > 0;

    if (hasErrors) {
      return false;
    } else {
      return true;
    }
  }

  // Create new group plugin or form submit
  function handleCreateGroupPlugin() {
    let isFormValid = validateForm();

    if (isFormValid) {
      const singleArray = newGroupPlugin.groups[0];

      const pluginData = {
        iboNumber: selectedOrg.iboNumber,
        plugin: {
          _id: newGroupPlugin._id,
          name: newGroupPlugin.name,
          iboNumber: newGroupPlugin.iboNumber,
          pinLevel: newGroupPlugin.pinLevel,
          organizationId: newGroupPlugin.organizationId || "",
          organizationName: newGroupPlugin.organizationName || "",

          groups: [singleArray],
        },
        sync: newPlugin.withoutRefresh,
        isAdmin: newPlugin.isAdmin,
      };
      onClick(pluginData);
      setIsLoadingData(!isLoadingData);
    }
  }

  return (
    <div className={styles.container}>
      <Modal
        title="ADD GROUP PLUGIN"
        isOpen={isOpen}
        onClose={onClose}
        closeBtn="none"
        className={styles.modal}
        children={
          <>
            <div className={styles.description}>
              Plug a user into a specific group.
            </div>
            <div className={styles.inputContainer}>
              <div className={styles.inputItem}>
                <div className={styles.label}> Name / IBO Number: </div>
                <div className={styles.inputText}>
                  <SuggestionInput
                    value={selectedIBO.length > 0 ? selectedIBO : editTerm}
                    autoFocus={false}
                    minLength={3}
                    debounceTime={500}
                    fetchSuggestions={setSearchTerm}
                    suggestions={searchResults}
                    isLoading={isLoading}
                    placeholder={
                      selectedIBO.length > 0 ? selectedIBO : "Search for IBO"
                    }
                    onChange={(e) => {
                      setEditTerm(e.target.value);
                    }}
                  >
                    {(item, closePanel) => {
                      const [, label, rest] = item.display.match(/^(.+):(.*)$/);
                      return (
                        <div
                          key={item.contactPK}
                          className={styles.iboListItem}
                        >
                          <div>
                            <span>{label}:</span>
                            <button
                              className={styles.iboItem}
                              onClick={() => {
                                iboSearchSelect(item);
                                setEditTerm("");
                                closePanel();
                              }}
                            >
                              {rest}
                            </button>
                          </div>
                        </div>
                      );
                    }}
                  </SuggestionInput>
                </div>

                <span className={styles.errorOrgMsg}>{errors.selectedIBO}</span>
              </div>

              <div className={styles.inputItemBottom}>
                <div className={styles.label}>Add To This Group:</div>

                <div className={styles.dropDownContainer}>
                  <div
                    className={styles.selected}
                    onClick={handleDropdownClick}
                  >
                    <input
                      type="text"
                      ref={ref}
                      placeholder={
                        selectedGroup.length > 0
                          ? selectedGroup.join(" > ")
                          : "Search by Organization, Group, or IBO #"
                      }
                      value={newFilter}
                      onChange={(e) => {
                        setNewFilter(e.target.value);
                      }}
                    />
                  </div>
                </div>

                {!loading && openDropDown && searchData.length > 0 && (
                  <div className={styles.selectOptions}>
                    <>
                      {searchData.map((item) => (
                        <div
                          className={styles.selectOption}
                          onClick={() => {
                            groupSearchSelect(item);
                          }}
                        >
                          <span>
                            {item.iboNumber} &gt; {item.organizationName} &gt;{" "}
                            {item.groups[0].groupName}
                          </span>
                        </div>
                      ))}
                    </>
                  </div>
                )}
              </div>
              <span className={styles.errorGroupMsg}>
                {errors.selectedGroup}
              </span>
            </div>
            <CheckBoxItem
              name="Sync"
              checked={newPlugin.withoutRefresh}
              onClick={() =>
                setNewPlugin({
                  ...newPlugin,
                  withoutRefresh: !newPlugin.withoutRefresh,
                })
              }
            />
            &nbsp;
            <CheckBoxItem
              name="Admin"
              checked={newPlugin.isAdmin}
              onClick={() =>
                setNewPlugin({
                  ...newPlugin,
                  isAdmin: !newPlugin.isAdmin,
                })
              }
            />
            {/* <ModalActionBar
              children={ */}
            <div className={styles.buttonContainer}>
              <button
                className={classNames(styles.button, styles.cancelBtn)}
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </button>
              <button
                className={styles.button}
                onClick={() => handleCreateGroupPlugin()}
              >
                Add
              </button>
            </div>
            {/* }
            /> */}
          </>
        }
      />
    </div>
  );
}

AddGroupPluginModal.propTypes = {
  /**
   * Boolean to determine if modal is open.
   */
  isOpen: PropTypes.bool,
  /**
   * Callback functino for when the modal is closed.
   */
  onClose: PropTypes.func,
  /**
   * Function called when the add button is clicked.
   * The function needs to validate the input values and add an ID value.
   */
  onClick: PropTypes.func,
};
